<template>
    <div
        class="loyalty-program col-span-12 md:col-span-6 lg:col-span-4 mb-5 relative overflow-visible rounded-lg bg-white dark:bg-slate-800 px-4 pt-5 pb-12 shadow dark:shadow-slate-900/50 sm:px-6 sm:pt-6 border border-slate-200 dark:border-slate-700"
    >
        <div
            class="absolute inset-0 flex flex-col justify-center items-center bg-slate-100/95 dark:bg-slate-900/95 z-30 px-6 rounded-lg"
            v-if="
                program.program_user && !program.program_user.converted_notified
            "
        >
            <div class="flex items-center flex-col">
                <img
                    v-if="program.image"
                    :src="getProgramImageUrl(program.image)"
                    class="object-contain mb-3"
                    style="max-height: 40px"
                />
                <h5 class="font-bold text-lg mb-2 sr-only">
                    {{ program.name }}
                </h5>
            </div>
            <p class="text-center text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                We've created this rewards program for you since
                {{ program.bank?.name }} does not sync your rewards balance with
                My10x.
            </p>
            <p class="text-center text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                You will have to
                <span class="font-bold">manually update the rewards.</span>
            </p>

            <button
                class="rounded bg-white dark:bg-slate-800 py-1 px-2 text-sm font-semibold text-slate-500 dark:text-slate-300 shadow-sm dark:shadow-slate-900/50 ring-1 ring-inset ring-slate-300 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
                @click="setConvertedNotified"
            >
                Ok got it
                <i class="fa-lg fa-solid fa-thumbs-up ml-1"></i>
            </button>
        </div>

        <div class="flex flex-col justify-between h-full mb-6">
            <div class="flex w-full flex-wrap mb-2 pb-12">
                <div class="program-logo w-24 flex-initial mr-3">
                    <img
                        v-if="program.image"
                        :src="getProgramImageUrl(program.image)"
                        class="object-contain"
                        style="max-height: 60px"
                    />
                </div>
                <div class="program-name flex-1 pr-16">
                    <p class="font-bold text-orange dark:text-white leading-none text-2xl mb-1">
                        {{ $filters.formatThousand(program.points) }}
                    </p>
                    <h3
                        class="text-sm font-medium text-slate-500 dark:text-slate-400 leading-tight mb-1"
                    >
                        {{ program.name }}
                    </h3>
                </div>
                <div class="absolute top-0 right-0 items-start gap-1 flex p-2">
                    <ProgramDropdown
                        :program="program"
                        :user="user"
                        iconClass="text-slate-500 dark:text-slate-400"
                    />
                    <button
                        @click="openHistorySidebar"
                        class="btn-icon btn-transparent"
                        title="View points history"
                    >
                        <Icon
                            icon="chevron-right"
                            class="w-5 h-5 text-slate-500 dark:text-slate-400"
                        />
                    </button>
                </div>

                <ProgramStatus :program="program" />
            </div>
            <div class="absolute inset-x-0 bottom-0">
                <div class="px-4 pb-2">
                    <UiTooltip :text="cppTooltipText" placement="bottom">
                        <template #trigger>
                            <div
                                class="px-3 py-1 bg-slate-100 dark:bg-slate-700 rounded-full inline-flex justify-start items-center gap-2 cursor-pointer"
                            >
                                <div
                                    class="flex justify-start items-center gap-1"
                                >
                                    <div
                                        class="justify-center text-slate-500 dark:text-slate-300 text-xs font-medium font-['Inter'] leading-none"
                                    >
                                        Avg CPP {{ formattedCpp }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </UiTooltip>
                </div>
                <div
                    class="flex w-full items-end justify-between p-4 bg-slate-50 dark:bg-slate-900/40 rounded-b-lg"
                >
                    <div class="program-type">
                        <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {{ programName(program.type) }}
                        </p>

                        <p class="text-xs text-slate-500 dark:text-slate-400">
                            Last updated on {{ program.last_updated_at }}
                        </p>
                    </div>
                    <div v-if="program.connected && !program.use_manual_points">
                        <div
                            class="flex items-center justify-center rounded-full !text-slate-800 dark:!text-slate-100 text-xs w-6 h-6 relative annual-fee-label-trigger cursor-pointer ml-2 z-30"
                        >
                            <span class="annual-fee-label text-xs">
                                Your {{ program.name }} account syncs your
                                rewards automatically.
                            </span>
                            <Icon
                                icon="refresh"
                                class="w-3.5 h-3.5 !text-slate-600 dark:!text-slate-300"
                            />
                        </div>
                    </div>
                    <div v-if="program.connected && program.use_manual_points">
                        <div
                            class="flex items-center justify-center rounded-full bg-green-200 !text-green-600 text-xs w-6 h-6 relative annual-fee-label-trigger cursor-pointer ml-2 z-30"
                        >
                            <span class="annual-fee-label text-xs">
                                Your {{ program.name }} account is available to
                                sync your rewards automatically. However, you
                                are currently managing this program.
                            </span>
                            <i
                                class="fa-regular fa-arrows-left-right !text-green-600 fa-sm"
                            ></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { usePage } from '@inertiajs/vue3';
import { usePointsStore } from '@/store/points';

import vClickOutside from 'click-outside-vue3';
import ProgramDropdown from '@/components/mypoints/program-dropdown.vue';
import ProgramStatus from '@/components/mypoints/program-status.vue';
import UiTooltip from '@/components/ui/tooltip/ui-tooltip.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { handleError } from '@/utils/error-handler';
import { Apii } from '@/services/api.js';

const ApiService = new Apii();

export default {
    components: {
        ProgramDropdown,
        ProgramStatus,
        UiTooltip,
        Icon,
    },
    directives: {
        ClickOutside: vClickOutside.directive,
    },
    props: {
        program: Object,
        user: Object,
    },
    data: () => ({
        errors: [],
        loading: false,
        programFilters: [
            { label: 'Airline', value: 'airline' },
            { label: 'Cashback', value: 'cashback' },
            { label: 'Hotels', value: 'hotel' },
            { label: 'Rail', value: 'rail' },
            { label: 'Flexible Currency', value: 'flexible_currency' },
        ],
    }),
    computed: {
        ...mapState(usePointsStore, ['cppData', 'cppThreshold']),
        cdnUrl() {
            return window.cdnUrl;
        },
        programCpp() {
            return this.cppData[this.program.id] || null;
        },
        hasCppData() {
            return this.programCpp && this.programCpp.cpp_cents != null;
        },
        formattedCpp() {
            if (!this.hasCppData) return '—';
            return parseFloat(this.programCpp.cpp_cents).toFixed(1);
        },
        cppTooltipText() {
            if (this.hasCppData) {
                return `${parseFloat(this.programCpp.cpp_cents).toFixed(1)} cents per point (CPP) - average value My10x users get with ${this.program.name}`;
            }
            return "Average cents per point (CPP) value isn't available for this program";
        },
    },
    methods: {
        ...mapActions(usePointsStore, [
            'getRewards',
            'openPointsHistorySidebar',
        ]),
        getProgramImageUrl(image) {
            if (!image) return '';
            // Handle both formats: program_images/ and programs_images/
            const cleanImage = image.replace(/^programs?_images\//, '');
            return `https://my10x.nyc3.cdn.digitaloceanspaces.com/uploads/program_images/_small/${cleanImage}`;
        },
        openHistorySidebar() {
            this.openPointsHistorySidebar(this.program, this.user);
        },
        programName(type) {
            let program = this.programFilters.find((program) => {
                return type === program.value;
            });
            return program ? program.label : '';
        },
        setConvertedNotified() {
            ApiService.apiCall('/programs/converted-notified', 'post', {
                program_id: this.program.id,
                user_id: this.user.id,
                wallet_id: this.program.program_user.wallet_id,
            })
                .then((response) => {
                    this.getRewards();
                })
                .catch((error) => {
                    handleError(error);
                });
        },
    },
};
</script>
<style lang="scss" scoped>
.annual-fee-label-trigger {
    &:hover {
        .annual-fee-label {
            display: block;
        }
    }
}
.annual-fee-label {
    display: none;
    width: 200px;
    padding: 6px 12px;
    color: #fff;
    background-color: #1c293d;
    border-radius: 4px;
    position: absolute;
    bottom: 110%;
    left: 50%;
    margin-left: -100px;
}
</style>
